import { useEffect, useState } from "react";

import { useTranslation } from "next-i18next";

import {
  hasWholeBundle,
  markWholeBundle,
} from "@/common/utils/i18n-bundles.utils";

type Resources = Record<string, unknown>;
type NamespaceLoader = (locale: string) => Promise<{ default: Resources }>;

/**
 * Namespaces that shell components may need on pages that did not request
 * them server-side. Each loader is a template dynamic import, so webpack
 * emits one small chunk per locale and nothing lands in the page HTML.
 *
 * Only add a namespace here when a component rendered on EVERY page (header,
 * hamburger, app-wide modals) reads it. Pages that own the copy keep passing
 * the namespace to `getTranslations` as before.
 */
const LOADERS = {
  account: (locale: string) =>
    import(`../../../../public/locales/${locale}/account.json`),
  // Open Home's "My Open Home" entry in the account menus (sidebar and drawer).
  "open-home": (locale: string) =>
    import(`../../../../public/locales/${locale}/open-home.json`),
} satisfies Record<string, NamespaceLoader>;

export type LazyNamespace = keyof typeof LOADERS;

/**
 * Makes `namespace` available to `t()` on the client when the current page did
 * not load it server-side, and reports when it is.
 *
 * WHO-5453 follow-up. The grouped account drawer opens from the hamburger on
 * any page, but only the account pages request the `account` namespace, so on
 * the homepage or a PDP every label rendered as its raw key
 * ("account:account-menu-group-selling"). `account.json` is 15 KB per locale;
 * adding it to `COMMON_NAMESPACES` would put that in the HTML of every page,
 * which `lib/getTranslations.js` explicitly forbids. Loading it on first use
 * costs one request for the users who open the menu and nothing for the rest.
 *
 * Returns `true` immediately when the page shipped the whole namespace (account
 * pages) and, in tests whose i18n mock has no resource store, so existing specs
 * keep rendering synchronously. Part of it is not enough: the product page
 * ships 13 `account` keys for its reserve modal, and trusting those left the
 * drawer on a product page showing raw keys (WHO-6644).
 */
export const useLazyNamespace = (
  namespace: LazyNamespace,
  loader: NamespaceLoader = LOADERS[namespace]
): boolean => {
  const { i18n } = useTranslation();
  const locale = i18n?.language;

  const isLoaded = () =>
    !i18n ||
    typeof i18n.addResourceBundle !== "function" ||
    !locale ||
    hasWholeBundle(i18n, locale, namespace);

  const [ready, setReady] = useState<boolean>(isLoaded);

  useEffect(() => {
    if (isLoaded()) {
      setReady(true);
      return;
    }

    let cancelled = false;

    loader(locale as string)
      .then(module => {
        if (cancelled) {
          return;
        }

        // deep merge on, overwrite off: never clobber strings the page
        // already brought along server-side.
        i18n.addResourceBundle(
          locale as string,
          namespace,
          module.default,
          true,
          false
        );
        markWholeBundle(i18n, locale as string, namespace);
        setReady(true);
      })
      .catch(() => {
        // A missing chunk must not hide the menu: fall back to keys/fallbacks.
        if (!cancelled) {
          setReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
    // `isLoaded` closes over the same three values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n, locale, namespace, loader]);

  return ready;
};
